

import React, { useCallback, useEffect, useState } from 'react';
import { DailyActivityWithAssignments, ProjectZone } from '@/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Skeleton } from '@/shared/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/alert';
import { ChevronLeft, ChevronRight, Terminal } from 'lucide-react';
import { MatrixCalendar } from '@/features/projects/components/MatrixCalendar';
import { upsertDailyActivityNotes } from '@/features/projects/api';
import { useSupabaseAuth } from '@/shared/hooks/useSupabaseAuth';

/**
 * 日次活動カレンダーのプロパティ
 */
interface DailyActivitySectionProps {
    projectId: string;
    zones: ProjectZone[];
    activities: DailyActivityWithAssignments[];
    isLoading: boolean;
    error: string | null;
    isReadOnly: boolean;
    currentYearMonth: string;
    onYearMonthChange: (yearMonth: string) => void;
    onResourceEditRequest: (date: string, zoneId: string | null) => void;
    onActivityUpdated?: () => void;
    currentUserCraftsmanId?: string | null;
}

/**
 * 日次活動カレンダー
 * @param param0 日次活動カレンダーのプロパティ
 * @returns 日次活動カレンダー
 */
export const DailyActivitySection: React.FC<DailyActivitySectionProps> = ({
    projectId,
    zones,
    activities,
    isLoading,
    error,
    isReadOnly,
    currentYearMonth,
    onYearMonthChange,
    onResourceEditRequest,
    onActivityUpdated,
    currentUserCraftsmanId: propCurrentUserCraftsmanId
}) => {
    const [year, month] = currentYearMonth.split('-').map(Number);
    const [currentUserCraftsmanId, setCurrentUserCraftsmanId] = useState<string | null>(null);
    const { user } = useSupabaseAuth();

    useEffect(() => {
        // プロパティで渡された値を優先し、なければuseSupabaseAuthから取得
        if (propCurrentUserCraftsmanId !== undefined) {
            setCurrentUserCraftsmanId(propCurrentUserCraftsmanId);
        } else if (user?.craftsman_id) {
            setCurrentUserCraftsmanId(user.craftsman_id);
        } else {
            setCurrentUserCraftsmanId(null);
        }
    }, [propCurrentUserCraftsmanId, user]);

    /**
     * 前月に移動するハンドラー
     */
    const handlePreviousMonth = useCallback(() => {
        let prevYear = year;
        let prevMonth = month - 1;

        if (prevMonth < 1) {
            prevMonth = 12;
            prevYear -= 1;
        }

        onYearMonthChange(`${prevYear}-${String(prevMonth).padStart(2, '0')}`);
    }, [year, month, onYearMonthChange]);

    /**
     * 次月に移動するハンドラー
     */
    const handleNextMonth = useCallback(() => {
        let nextYear = year;
        let nextMonth = month + 1;

        if (nextMonth > 12) {
            nextMonth = 1;
            nextYear += 1;
        }

        onYearMonthChange(`${nextYear}-${String(nextMonth).padStart(2, '0')}`);
    }, [year, month, onYearMonthChange]);

    /**
     * 月の名前を取得
     * @param monthNum 月の番号
     * @returns 月の名前
     */
    const getMonthName = (monthNum: number): string => {
        const monthNames = [
            '1月', '2月', '3月', '4月', '5月', '6月',
            '7月', '8月', '9月', '10月', '11月', '12月'
        ];
        return monthNames[monthNum - 1];
    };

    /**
     * 日次活動カレンダーのノートを更新
     * @param date 日付
     * @param zoneId ゾーンID
     * @param notes ノート
     * @returns 更新の成否
     */
    const handleNotesUpdate = async (
        date: string,
        zoneId: string | null,
        notes: string
    ): Promise<boolean> => {
        try {
            const result = await upsertDailyActivityNotes({
                projectId,
                date,
                zoneId,
                notes
            });

            if (result.error) {
                throw new Error(result.error.message);
            }

            // 親コンポーネントに通知（必要に応じて活動データを再取得するため）
            if (onActivityUpdated) {
                await onActivityUpdated();
            }

            return true;
        } catch (error) {
            console.error('Failed to update notes:', error);
            return false;
        }
    };

    return (
        <Card className="border shadow-sm">
            <CardHeader className="pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <CardTitle className="fluid-sm md:text-lg">日次活動カレンダー</CardTitle>

                    <div className="flex items-center gap-1 bg-gray-50 dark:bg-gray-800 rounded-lg p-1">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handlePreviousMonth}
                            className="h-8 w-8 p-0"
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </Button>

                        <Button
                            variant="ghost"
                            size="sm"
                            disabled
                            className="min-w-[100px] h-8 text-sm font-medium"
                        >
                            {year}年{getMonthName(month)}
                        </Button>

                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleNextMonth}
                            className="h-8 w-8 p-0"
                        >
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </CardHeader>

            <CardContent className="pt-0">
                {isLoading ? (
                    <div className="space-y-2 animate-pulse">
                        <Skeleton className="h-8 w-full" />
                        <Skeleton className="h-32 w-full" />
                    </div>
                ) : error ? (
                    <Alert variant="destructive">
                        <Terminal className="h-4 w-4" />
                        <AlertTitle>エラーが発生しました</AlertTitle>
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                ) : (
                    <MatrixCalendar
                        year={year}
                        month={month}
                        zones={zones}
                        activities={activities}
                        isReadOnly={isReadOnly}
                        onResourceEditRequest={onResourceEditRequest}
                        onNotesUpdate={handleNotesUpdate}
                        currentUserCraftsmanId={currentUserCraftsmanId}
                    />
                )}
            </CardContent>
        </Card>
    );
}; 